import {
  type ReactNode,
  type RefCallback,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { MenuItem } from '@dataroom/ui';
import { useIxT } from '../i18n';
import '../styles.css';
import {
  announce,
  makeAutoScroll,
  makeDraggable,
  makeDropTarget,
  scrollParent,
  setKeyboardDrag,
} from './engine';
import {
  type Axis,
  type DragKind,
  type DragPayload,
  type Edge,
  type ListKeyboardState,
  type Point,
  axisEdges,
  closestEdge,
  listKeyboardReducer,
  previewOrder,
  reorderDestination,
} from './model';

// -- useDraggable / useDropTarget ------------------------------------------------------------------

export type UseDraggableOptions = {
  payload: () => DragPayload;
  /** Native data for other tabs, windows and apps (`{ mime: value }`, e.g. `nativeDragData(item)`). */
  external?: () => Record<string, string>;
  /** Touch drags start only on the handle; mouse drags anywhere on the element unless a handle is set. */
  handle?: RefObject<HTMLElement | null>;
  disabled?: boolean;
  onStart?: (mode: 'pointer' | 'touch') => void;
  onEnd?: (dropped: boolean) => void;
};

/** Make the element behind `ref` draggable (mouse, pen, touch). */
export function useDraggable<E extends HTMLElement>(
  ref: RefObject<E | null>,
  options: UseDraggableOptions,
): { dragging: boolean } {
  const latest = useRef(options);
  latest.current = options;
  const [dragging, setDragging] = useState(false);
  const { disabled } = options;
  useEffect(() => {
    const element = ref.current;
    if (!element || disabled) return;
    return makeDraggable({
      element,
      handle: latest.current.handle?.current ?? null,
      payload: () => latest.current.payload(),
      external: () => latest.current.external?.() ?? {},
      onStart: (mode) => {
        setDragging(true);
        latest.current.onStart?.(mode);
      },
      onEnd: (dropped) => {
        setDragging(false);
        latest.current.onEnd?.(dropped);
      },
    });
  }, [ref, disabled]);
  return { dragging };
}

export type UseDropTargetOptions<K extends DragKind> = {
  accepts: readonly K[];
  canDrop?: (payload: DragPayload<K>) => boolean;
  onDrag?: (payload: DragPayload<K>, point: Point, rect: DOMRect) => void;
  onLeave?: () => void;
  onDrop: (payload: DragPayload<K>, point: Point, rect: DOMRect) => void;
  disabled?: boolean;
};

/** Accept typed payloads on the element behind `ref`. `over` is true while an accepted drag hovers it. */
export function useDropTarget<K extends DragKind, E extends HTMLElement = HTMLElement>(
  ref: RefObject<E | null>,
  options: UseDropTargetOptions<K>,
): { over: boolean } {
  const latest = useRef(options);
  latest.current = options;
  const [over, setOver] = useState(false);
  const { disabled } = options;
  useEffect(() => {
    const element = ref.current;
    if (!element || disabled) return;
    const accepts = (p: DragPayload): boolean =>
      (latest.current.accepts as readonly string[]).includes(p.kind) &&
      (latest.current.canDrop?.(p as DragPayload<K>) ?? true);
    return makeDropTarget({
      element,
      accepts,
      onDrag: (payload, point) => {
        setOver(true);
        latest.current.onDrag?.(payload as DragPayload<K>, point, element.getBoundingClientRect());
      },
      onLeave: () => {
        setOver(false);
        latest.current.onLeave?.();
      },
      onDrop: (payload, point) => {
        setOver(false);
        latest.current.onDrop(payload as DragPayload<K>, point, element.getBoundingClientRect());
      },
    });
  }, [ref, disabled]);
  return { over };
}

// -- DropIndicator ---------------------------------------------------------------------------------

/**
 * The one drop indicator: a 2 px accent line with a terminal dot on the edge of the item (render it
 * inside a `position: relative` item). `gap` is the space between items, so the line sits in the middle.
 */
export function DropIndicator({ edge, gap = 0 }: { edge: Edge; gap?: number }) {
  return (
    <span
      className="dr-drop-indicator"
      data-edge={edge}
      aria-hidden="true"
      style={{ ['--dr-drop-gap' as string]: `${gap}px` }}
    />
  );
}

/** Visually hidden text (descriptions for drag handles). */
export function VisuallyHidden({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <span id={id} className="dr-sr-only">
      {children}
    </span>
  );
}

// -- useSortableList -------------------------------------------------------------------------------

export type SortableListOptions<T> = {
  /** Unique per list on the page (payloads from other lists are ignored). */
  id: string;
  items: readonly T[];
  getKey: (item: T) => string;
  /** Spoken name of an item ("Amount"). */
  getLabel: (item: T) => string;
  /** Commit a reorder (mouse, touch, keyboard and the move menu all end here). */
  onReorder: (from: number, to: number) => void;
  axis?: Axis;
  disabled?: boolean;
  /** Mouse drags start anywhere on the item (default) or only on the handle. Touch always uses the handle. */
  dragFrom?: 'item' | 'handle';
};

export type SortableItemProps = {
  ref: RefCallback<HTMLElement>;
  'data-sortable-item': '';
  'data-dragging'?: '';
  'data-lifted'?: '';
  'data-drop-edge'?: Edge;
};

export type SortableHandleProps = {
  ref: RefCallback<HTMLElement>;
  role: 'button';
  tabIndex: 0 | -1;
  'aria-label': string;
  'aria-describedby': string;
  'aria-pressed': boolean;
  'data-sortable-handle': '';
  onKeyDown: (event: React.KeyboardEvent) => void;
  onBlur: () => void;
};

export type SortableList<T> = {
  /** Items in display order (a lifted keyboard drag previews its new position). */
  ordered: Array<{ item: T; key: string; index: number }>;
  itemProps: (key: string) => SortableItemProps;
  handleProps: (key: string) => SortableHandleProps;
  /** The edge to draw the drop line on for this item (pointer and touch drags). */
  indicator: (key: string) => Edge | null;
  /** Hidden description for the handles plus nothing visible; render once inside the list. */
  description: ReactNode;
  /** "Move up / down / to top / to bottom" menu items for an item (keyboard and screen-reader path). */
  moveItems: (key: string) => MenuItem[];
  /** Move by keyboard (⌥↑ / ⌥↓ on an item). */
  moveBy: (key: string, delta: number) => void;
  lifted: string | null;
  dragging: string | null;
};

/**
 * A sortable list: drag by mouse, pen or touch from the handle, or by keyboard on the handle (Space
 * lifts, arrows move, Space drops, Escape cancels), with one drop line, autoscroll and localized
 * announcements (`ix.dnd.*`).
 */
export function useSortableList<T>(options: SortableListOptions<T>): SortableList<T> {
  const t = useIxT();
  const { id: listId, items, getKey, axis = 'vertical', disabled } = options;
  const latest = useRef(options);
  latest.current = options;
  const keys = useMemo(() => items.map(getKey), [items, getKey]);
  const keysRef = useRef(keys);
  keysRef.current = keys;
  const [kb, setKb] = useState<ListKeyboardState>({ phase: 'idle' });
  const kbRef = useRef(kb);
  kbRef.current = kb;
  const [drop, setDrop] = useState<{ key: string; edge: Edge } | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const hintId = `dr-sortable-hint-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const handles = useRef(new Map<string, HTMLElement>());
  const cleanups = useRef(new Map<string, () => void>());
  const elements = useRef(new Map<string, HTMLElement>());

  const labelOf = useCallback((key: string) => {
    const item = latest.current.items.find((i) => latest.current.getKey(i) === key);
    return item === undefined ? key : latest.current.getLabel(item);
  }, []);

  const commit = useCallback((from: number, to: number) => {
    if (from !== to) latest.current.onReorder(from, to);
  }, []);

  // Attach drag and drop to an item once both its element and handle are known.
  const wire = useCallback(
    (key: string) => {
      cleanups.current.get(key)?.();
      cleanups.current.delete(key);
      const element = elements.current.get(key);
      if (!element || latest.current.disabled) return;
      const handle = handles.current.get(key) ?? null;
      const edges = axisEdges(latest.current.axis ?? 'vertical');
      const offDrag = makeDraggable({
        element,
        handle,
        pointerFrom: latest.current.dragFrom === 'handle' ? 'handle' : 'element',
        payload: () => ({
          kind: 'list-item',
          data: { listId, key, index: keysRef.current.indexOf(key) },
        }),
        canDrag: () => kbRef.current.phase === 'idle',
        onStart: () => setDragging(key),
        onEnd: () => {
          setDragging(null);
          setDrop(null);
        },
      });
      const offDrop = makeDropTarget({
        element,
        accepts: (p) => p.kind === 'list-item' && p.data.listId === listId,
        onDrag: (_p, point) => {
          const edge = closestEdge(element.getBoundingClientRect(), point, edges);
          setDrop((d) => (d?.key === key && d.edge === edge ? d : edge ? { key, edge } : null));
        },
        onLeave: () => setDrop((d) => (d?.key === key ? null : d)),
        onDrop: (p, point) => {
          setDrop(null);
          if (p.kind !== 'list-item') return;
          const from = keysRef.current.indexOf(p.data.key);
          const target = keysRef.current.indexOf(key);
          if (from < 0 || target < 0) return;
          const edge = closestEdge(element.getBoundingClientRect(), point, edges);
          const to = reorderDestination(from, target, edge, latest.current.axis ?? 'vertical');
          if (to === from) return;
          commit(from, to);
          announce(
            t('ix.dnd.dropped', {
              item: labelOf(p.data.key),
              position: to + 1,
              total: keysRef.current.length,
            }),
          );
        },
      });
      cleanups.current.set(key, () => {
        offDrag();
        offDrop();
      });
    },
    [listId, commit, labelOf, t],
  );

  // Re-wire everything when the list is (dis)abled or changes direction.
  useEffect(() => {
    for (const key of elements.current.keys()) wire(key);
  }, [wire, disabled, axis]);

  useEffect(
    () => () => {
      for (const c of cleanups.current.values()) c();
      cleanups.current.clear();
    },
    [],
  );

  // Autoscroll the list's scroll container during pointer drags.
  const firstKey = keys[0];
  useEffect(() => {
    if (disabled || firstKey === undefined) return;
    const scroller = scrollParent(elements.current.get(firstKey) ?? null);
    return scroller ? makeAutoScroll(scroller) : undefined;
  }, [disabled, firstKey]);

  const refCache = useRef(
    new Map<string, { item: RefCallback<HTMLElement>; handle: RefCallback<HTMLElement> }>(),
  );
  const refsFor = useCallback(
    (key: string) => {
      let refs = refCache.current.get(key);
      if (!refs) {
        refs = {
          item: (el) => {
            if (el) {
              elements.current.set(key, el);
              wire(key);
            } else if (elements.current.get(key)) {
              elements.current.delete(key);
              cleanups.current.get(key)?.();
              cleanups.current.delete(key);
            }
          },
          handle: (el) => {
            if (el) handles.current.set(key, el);
            else handles.current.delete(key);
            if (elements.current.has(key)) wire(key);
          },
        };
        refCache.current.set(key, refs);
      }
      return refs;
    },
    [wire],
  );
  // New wire function (labels, list id): drop cached refs so React re-attaches.
  useEffect(() => {
    refCache.current.clear();
  }, [wire]);

  const announceResult = useCallback(
    (result: ReturnType<typeof listKeyboardReducer>['result']) => {
      if (!result) return;
      const item = labelOf(result.key);
      if (result.type === 'lifted')
        announce(t('ix.dnd.lifted', { item, position: result.position, total: result.total }));
      else if (result.type === 'moved')
        announce(t('ix.dnd.moved', { item, position: result.position, total: result.total }));
      else if (result.type === 'dropped')
        announce(t('ix.dnd.dropped', { item, position: result.to + 1, total: result.total }));
      else announce(t('ix.dnd.cancelled', { item, position: result.from + 1, total: result.total }));
    },
    [labelOf, t],
  );

  const dispatchKb = useCallback(
    (action: Parameters<typeof listKeyboardReducer>[1]) => {
      const { state, result } = listKeyboardReducer(kbRef.current, action);
      kbRef.current = state;
      setKb(state);
      setKeyboardDrag(
        state.phase === 'lifted'
          ? { kind: 'list-item', data: { listId, key: state.key, index: state.from } }
          : null,
      );
      if (result?.type === 'dropped') commit(result.from, result.to);
      announceResult(result);
      if (result && (result.type === 'dropped' || result.type === 'moved' || result.type === 'lifted')) {
        // Keep focus on the moved handle after React re-renders the list in its new order.
        const key = result.key;
        requestAnimationFrame(() => handles.current.get(key)?.focus({ preventScroll: false }));
      }
    },
    [listId, commit, announceResult],
  );

  const moveBy = useCallback(
    (key: string, delta: number) => {
      const from = keysRef.current.indexOf(key);
      const to = Math.max(0, Math.min(keysRef.current.length - 1, from + delta));
      if (from < 0 || to === from) return;
      commit(from, to);
      announce(t('ix.dnd.dropped', { item: labelOf(key), position: to + 1, total: keysRef.current.length }));
    },
    [commit, labelOf, t],
  );

  const ordered = useMemo(() => {
    const order = previewOrder(keys, kb);
    const byKey = new Map(items.map((item, index) => [keys[index]!, { item, index }]));
    return order.map((key) => ({ key, ...byKey.get(key)! }));
  }, [items, keys, kb]);

  const prevKey = axis === 'vertical' ? 'ArrowUp' : 'ArrowLeft';
  const nextKey = axis === 'vertical' ? 'ArrowDown' : 'ArrowRight';

  return {
    ordered,
    lifted: kb.phase === 'lifted' ? kb.key : null,
    dragging,
    description: <VisuallyHidden id={hintId}>{t('ix.dnd.handleHint')}</VisuallyHidden>,
    indicator: (key) => (drop?.key === key && dragging !== key ? drop.edge : null),
    itemProps: (key) => ({
      ref: refsFor(key).item,
      'data-sortable-item': '',
      ...(dragging === key ? { 'data-dragging': '' as const } : {}),
      ...(kb.phase === 'lifted' && kb.key === key ? { 'data-lifted': '' as const } : {}),
      ...(drop?.key === key && dragging !== key ? { 'data-drop-edge': drop.edge } : {}),
    }),
    handleProps: (key) => ({
      ref: refsFor(key).handle,
      role: 'button',
      tabIndex: disabled ? -1 : 0,
      'aria-label': t('ix.dnd.handle', { item: labelOf(key) }),
      'aria-describedby': hintId,
      'aria-pressed': kb.phase === 'lifted' && kb.key === key,
      'data-sortable-handle': '',
      onKeyDown: (event) => {
        if (disabled) return;
        const state = kbRef.current;
        const lifted = state.phase === 'lifted' && state.key === key;
        const index = keysRef.current.indexOf(key);
        if (!lifted) {
          if (event.key === ' ' || event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            dispatchKb({ type: 'lift', key, index, count: keysRef.current.length });
          } else if (event.altKey && (event.key === prevKey || event.key === nextKey)) {
            event.preventDefault();
            moveBy(key, event.key === prevKey ? -1 : 1);
          }
          return;
        }
        // While lifted the handle owns the keyboard (Escape cancels the drag, not the popover).
        const handled = () => {
          event.preventDefault();
          event.stopPropagation();
        };
        if (event.key === ' ' || event.key === 'Enter') {
          handled();
          dispatchKb({ type: 'drop' });
        } else if (event.key === 'Escape') {
          handled();
          dispatchKb({ type: 'cancel' });
        } else if (event.key === prevKey) {
          handled();
          dispatchKb({ type: 'move', delta: -1 });
        } else if (event.key === nextKey) {
          handled();
          dispatchKb({ type: 'move', delta: 1 });
        } else if (event.key === 'Home') {
          handled();
          dispatchKb({ type: 'moveTo', index: 0 });
        } else if (event.key === 'End') {
          handled();
          dispatchKb({ type: 'moveTo', index: keysRef.current.length - 1 });
        } else if (event.key === 'Tab') {
          // Leaving the handle drops where the item is now.
          dispatchKb({ type: 'drop' });
        }
      },
      onBlur: () => {
        const state = kbRef.current;
        if (state.phase === 'lifted' && state.key === key) {
          // Re-rendering moves the node; only cancel when focus really left the list.
          requestAnimationFrame(() => {
            const active = document.activeElement;
            if (kbRef.current.phase === 'lifted' && active !== handles.current.get(key))
              dispatchKb({ type: 'cancel' });
          });
        }
      },
    }),
    moveBy,
    moveItems: (key) => {
      const index = keys.indexOf(key);
      const last = keys.length - 1;
      return [
        {
          label: t('ix.cmd.moveUp'),
          disabled: index <= 0,
          shortcut: 'alt+arrowup',
          onSelect: () => moveBy(key, -1),
        },
        {
          label: t('ix.cmd.moveDown'),
          disabled: index >= last,
          shortcut: 'alt+arrowdown',
          onSelect: () => moveBy(key, 1),
        },
        { label: t('ix.cmd.moveTop'), disabled: index <= 0, onSelect: () => moveBy(key, -index) },
        { label: t('ix.cmd.moveBottom'), disabled: index >= last, onSelect: () => moveBy(key, last - index) },
      ];
    },
  };
}
